import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowRight, Search, Filter, ChevronDown, AlertTriangle, Sparkles, CheckCircle2,
  Heart, Scale, GraduationCap, Hotel, ShoppingCart, UtensilsCrossed,
  Building2, Factory, Car, Calculator, Sprout, Dumbbell, Truck,
  Landmark, Megaphone, Users, X, Wifi,
  Globe, Bot, PhoneCall, MessageCircle
} from "lucide-react";
import { industries } from "@/data/industries";

const iconMap = {
  Heart, Scale, GraduationCap, Hotel, ShoppingCart, UtensilsCrossed,
  Building2, Factory, Car, Calculator, Sprout, Dumbbell, Truck,
  Landmark, Megaphone, Users, Wifi
};

export default function ServicesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");

  useEffect(() => {
    const industryParam = searchParams.get("industry");
    if (industryParam) {
      const found = industries.find((i) => i.id === industryParam);
      if (found) setSelectedIndustry(found);
    }
  }, [searchParams]);

  const filteredIndustries = industries.filter((industry) => {
    const matchesSearch =
      searchQuery === "" ||
      industry.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      industry.segments.some(
        (s) =>
          s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.pain.toLowerCase().includes(searchQuery.toLowerCase())
      );
    const matchesFilter =
      filterCategory === "all" ||
      (filterCategory === "featured" && industry.featured);
    return matchesSearch && matchesFilter;
  });

  const handleSelectIndustry = (industry) => {
    setSelectedIndustry(industry);
    setSearchParams({ industry: industry.id });
  };

  const handleClearSelection = () => {
    setSelectedIndustry(null);
    setSearchParams({});
  };

  const IconComponent = selectedIndustry
    ? iconMap[selectedIndustry.icon] || Heart
    : null;

  return (
    <div data-testid="services-page">
      {/* Header */}
      <section className="page-header-gradient border-b border-slate-100 py-16 md:py-24 relative overflow-hidden">
        <div className="hero-glow absolute inset-0" />
        <div className="dot-grid absolute inset-0 opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Badge
            variant="outline"
            className="mb-4 px-4 py-1.5 text-blue-600 border-blue-200 bg-white/80 font-semibold backdrop-blur-sm"
          >
            17+ Industries Covered
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-4">
            AI Solutions by Industry
          </h1>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">
            Every industry has unique pain points. We've mapped 90+ specific use cases
            across 17 sectors. Find yours below.
          </p>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="sticky top-16 z-40 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="services-search-input"
                placeholder="Search industries, pain points, or solutions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-10 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="border-slate-200 text-slate-700 h-10"
                  data-testid="services-filter-dropdown"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  {filterCategory === "all" ? "All Industries" : "Featured"}
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => setFilterCategory("all")}
                  data-testid="filter-all"
                >
                  All Industries
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setFilterCategory("featured")}
                  data-testid="filter-featured"
                >
                  Featured Industries
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {selectedIndustry ? (
          /* Industry Detail View */
          <div data-testid="industry-detail-view">
            <button
              onClick={handleClearSelection}
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-8 transition-colors"
              data-testid="back-to-all-btn"
            >
              <X className="w-4 h-4" />
              Back to all industries
            </button>

            <div className="flex items-center gap-4 mb-8">
              {IconComponent && (
                <div className={`w-14 h-14 rounded-lg ${selectedIndustry.bgColor} flex items-center justify-center`}>
                  <IconComponent className={`w-7 h-7 ${selectedIndustry.color}`} strokeWidth={1.5} />
                </div>
              )}
              <div>
                <h2 className="font-heading text-3xl font-bold text-slate-900">
                  {selectedIndustry.name}
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  {selectedIndustry.segments.length} automation use cases identified
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {selectedIndustry.segments.map((segment, idx) => (
                <Card
                  key={segment.name}
                  className="bg-white border border-slate-200 hover:shadow-md transition-all duration-300"
                  data-testid={`segment-card-${idx}`}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                      <div className="flex-1">
                        <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">
                          {segment.name}
                        </h3>
                        <div className="flex items-start gap-3 mb-3">
                          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">
                              Pain Point
                            </p>
                            <p className="text-sm text-slate-600">{segment.pain}</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Sparkles className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">
                              Our Solution
                            </p>
                            <p className="text-sm text-slate-700 font-medium">{segment.solution}</p>
                          </div>
                        </div>
                      </div>
                      <Link to="/contact" className="shrink-0">
                        <Button
                          variant="outline"
                          className="border-blue-200 text-blue-600 hover:bg-blue-50 text-sm"
                          data-testid={`segment-cta-${idx}`}
                        >
                          Get Quote
                          <ArrowRight className="w-3.5 h-3.5 ml-1" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-12 p-8 bg-blue-50 rounded-lg border border-blue-100 text-center">
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                Don't see your exact use case?
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                We build custom solutions. Tell us about your workflow and we'll engineer
                the automation.
              </p>
              <Link to="/contact">
                <Button className="bg-blue-600 text-white hover:bg-blue-700 font-semibold">
                  Describe Your Use Case
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          /* Industries Grid */
          <div data-testid="industries-grid">
            <p className="text-sm text-slate-500 mb-6">
              Showing {filteredIndustries.length} of {industries.length} industries
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filteredIndustries.map((industry) => {
                const Icon = iconMap[industry.icon] || Heart;
                return (
                  <Card
                    key={industry.id}
                    className="bg-white border border-slate-200 hover:border-blue-200 hover:shadow-card-hover transition-all duration-300 cursor-pointer group card-lift card-accent-blue"
                    onClick={() => handleSelectIndustry(industry)}
                    data-testid={`services-industry-card-${industry.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className={`w-10 h-10 rounded-xl ${industry.bgColor} flex items-center justify-center group-hover:scale-110 transition-transform duration-200`}>
                          <Icon className={`w-5 h-5 ${industry.color}`} strokeWidth={1.5} />
                        </div>
                        {industry.featured && (
                          <Badge className="bg-blue-50 text-blue-600 border-blue-200 text-xs font-semibold" variant="outline">
                            Featured
                          </Badge>
                        )}
                      </div>
                      <h3 className="font-heading text-base font-semibold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">
                        {industry.name}
                      </h3>
                      <p className="text-xs text-slate-400 font-medium mb-3">
                        {industry.segments.length} use cases
                      </p>
                      <ul className="space-y-1.5">
                        {industry.segments.slice(0, 2).map((seg) => (
                          <li key={seg.name} className="flex items-start gap-1.5 text-xs text-slate-500">
                            <CheckCircle2 className="w-3 h-3 text-teal-500 mt-0.5 shrink-0" />
                            <span className="line-clamp-1">{seg.solution}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-3 flex items-center text-xs font-semibold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity gap-1">
                        View all solutions
                        <ArrowRight className="w-3 h-3" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Core Services Section */}
      <section className="section-gradient-light border-t border-slate-100 py-16" data-testid="core-services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <Badge
              variant="outline"
              className="mb-4 px-4 py-1.5 text-teal-700 border-teal-200 bg-teal-50 font-medium"
            >
              Core Services
            </Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-3">
              What We Build For You
            </h2>
            <p className="text-base text-slate-600 max-w-2xl">
              End-to-end digital products and automation channels delivered across any industry.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Website Creation */}
            <Card
              className="bg-white border border-slate-200 hover:border-blue-200 hover:shadow-card-hover transition-all duration-300 card-lift card-accent-blue"
              data-testid="core-service-website"
            >
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl icon-bg-blue flex items-center justify-center mb-4">
                  <Globe className="w-6 h-6 text-blue-600" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                  Website Creation
                </h3>
                <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                  Custom business websites built for speed, SEO, and conversions — branded to your identity.
                </p>
                <ul className="space-y-1.5 mb-5">
                  {["Landing pages & full websites", "Mobile-first responsive design", "CMS integration & easy edits"].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-slate-500">
                      <CheckCircle2 className="w-3.5 h-3.5 text-teal-600 mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/contact">
                  <Button variant="outline" className="w-full border-blue-200 text-blue-600 hover:bg-blue-50 text-sm" data-testid="core-service-website-cta">
                    Get Started <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Chatbot Integration */}
            <Card
              className="bg-white border border-slate-200 hover:border-teal-200 hover:shadow-card-hover transition-all duration-300 card-lift card-accent-teal"
              data-testid="core-service-chatbot"
            >
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl icon-bg-teal flex items-center justify-center mb-4">
                  <Bot className="w-6 h-6 text-teal-600" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                  Chatbot Integration
                </h3>
                <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                  AI chatbots trained on your business data — answering queries, booking appointments, and qualifying leads 24/7.
                </p>
                <ul className="space-y-1.5 mb-5">
                  {["Trained on your FAQs & products", "Website & app embed", "Handoff to human agent"].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-slate-500">
                      <CheckCircle2 className="w-3.5 h-3.5 text-teal-600 mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/contact">
                  <Button variant="outline" className="w-full border-blue-200 text-blue-600 hover:bg-blue-50 text-sm" data-testid="core-service-chatbot-cta">
                    Get Started <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Automated Voice Call Agent */}
            <Card
              className="bg-white border border-slate-200 hover:border-violet-200 hover:shadow-card-hover transition-all duration-300 card-lift card-accent-violet"
              data-testid="core-service-voice"
            >
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl icon-bg-violet flex items-center justify-center mb-4">
                  <PhoneCall className="w-6 h-6 text-violet-600" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                  Automated Voice Call Agent
                </h3>
                <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                  AI voice agents that call leads, send reminders, and collect information — with natural, human-like conversation.
                </p>
                <ul className="space-y-1.5 mb-5">
                  {["Outbound call campaigns", "Appointment & payment reminders", "Multilingual voice support"].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-slate-500">
                      <CheckCircle2 className="w-3.5 h-3.5 text-teal-600 mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/contact">
                  <Button variant="outline" className="w-full border-blue-200 text-blue-600 hover:bg-blue-50 text-sm" data-testid="core-service-voice-cta">
                    Get Started <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* WhatsApp Integration */}
            <Card
              className="bg-white border border-slate-200 hover:border-emerald-200 hover:shadow-card-hover transition-all duration-300 card-lift card-accent-teal"
              data-testid="core-service-whatsapp"
            >
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-xl icon-bg-emerald flex items-center justify-center mb-4">
                  <MessageCircle className="w-6 h-6 text-emerald-600" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                  WhatsApp Integration
                </h3>
                <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                  Automate customer communication on WhatsApp — order updates, support, reminders, and marketing broadcasts.
                </p>
                <ul className="space-y-1.5 mb-5">
                  {["WhatsApp Business API setup", "Automated order & status updates", "Broadcast campaigns & drip flows"].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-slate-500">
                      <CheckCircle2 className="w-3.5 h-3.5 text-teal-600 mt-0.5 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/contact">
                  <Button variant="outline" className="w-full border-blue-200 text-blue-600 hover:bg-blue-50 text-sm" data-testid="core-service-whatsapp-cta">
                    Get Started <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
